import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config.json';
import searchInTheTable from '../../hooks/SarchInTable';
import filterBy from '../../hooks/FilterBy';

// New components
import SubTitle from '../common/SubTitle';
import Pagination from '../common/Pagination';
import SearchFilter from '../common/SearchFilter';
import RoleAdd from './RoleAdd';
import RoleTable from './RoleTable';

//icons
import Update_Icon from '../../images/table/Update_icon.svg'
import Delete_Icon from '../../images/table/Delete_icon.svg'
import Assign_Icon from '../../images/table/Assign_icon.svg'

import DeleteModal from '../common/DeleteModal';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

// Table headers
const headers = ["RoleAccess", "Description", "Access", "Actions"];

// API functions
async function getAllRoles() {
  try {
    const response = await axios.get(`${config.baseURL}/role/organization/`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
}

async function createRole(role) {
  try {
    const response = await axios.post(`${config.baseURL}/role`, role, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating role:", error);
    throw error;
  }
}

async function updateRole(role) {
  try {
    const response = await axios.put(`${config.baseURL}/role`, role, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating role:", error);
    throw error;
  }
}

async function deleteRole(id) {
  try {
    const response = await axios.delete(`${config.baseURL}/role/${id}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting role:", error);
    throw error;
  }
}

export default function RoleMain() {
  const { t } = useTranslation()
  const [roleList, setRoleList] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();

  // New state for inline editing
  const [editableRowIndex, setEditableRowIndex] = useState(null);
  const [editedRole, setEditedRole] = useState(null);
  const [tableData, setTableData] = useState([]);

  // State for delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  // Track window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatAccessFeatures = (features) => {
    if (!Array.isArray(features) || features.length === 0) {
      return "No access assigned";
    }

    // Get the first 3 feature names - properly handling the nested structure
    const displayFeatures = features.slice(0, 3).map(feature =>
      // Check if feature has a nested 'feature' object with a name property
      (feature.feature && feature.feature.name)
        ? feature.feature.name
        : (typeof feature === 'object' && feature.name)
          ? feature.name
          : feature
    );

    // Create display string with count of additional features
    let displayText = displayFeatures.join(', ');
    if (features.length > 3) {
      displayText += ` +${features.length - 3} more`;
    }

    return displayText;
  };

  // Format data for the table display
  const formatDataForTable = () => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    const paginatedData = filteredRoles.slice(startIdx, startIdx + itemsPerPage);
    return paginatedData.map(role => ({
      RoleAccess: role.name,
      Description: role.description,
      Access: formatAccessFeatures(role.features),
      id: role.id,
      _features: role.features
    }));
  };

  // Update table data whenever filteredRoles or pagination changes
  useEffect(() => {
    setTableData(formatDataForTable());
  }, [filteredRoles, currentPage, itemsPerPage]);

  // Handle search input changes
  const handleSearchChange = (value) => {
    setSearchText(value);
    if (roleList.length > 0) {
      const searchedRoles = searchInTheTable(value, roleList);
      setFilteredRoles(searchedRoles);
    }
  };

  // Handle filter changes
  const handleFilterChange = (value) => {
    setFilterValue(value);
    if (roleList.length > 0) {
      const filteredData = filterBy(value, 'name', roleList);
      setFilteredRoles(filteredData);
    }
  };

  // Handle actions (edit, delete)
  const handleActionClick = async (actionId, rowIndex, actionObj) => {
    // Get current table data
    const currentTableData = formatDataForTable();
    const rowData = currentTableData[rowIndex];

    switch (actionId) {
      case 'edit':
        // Find the full role object from the filteredRoles
        const roleToEdit = filteredRoles.find(role => role.id === rowData.id);
        if (roleToEdit) {
          setEditableRowIndex(rowIndex);
          setEditedRole({
            id: roleToEdit.id,
            name: roleToEdit.name,
            description: roleToEdit.description,
            features: roleToEdit.features
          });

          // Create a copy of the current table data for editing
          const updatedTableData = [...tableData];
          updatedTableData[rowIndex] = {
            ...updatedTableData[rowIndex],
            RoleAccess: roleToEdit.name,
            Description: roleToEdit.description
          };
          setTableData(updatedTableData);
        }
        break;
      case 'delete':
        // Find the full role object from the filteredRoles
        const roleToDelete = filteredRoles.find(role => role.id === rowData.id);
        if (roleToDelete) {
          setRoleToDelete(roleToDelete);
          setIsDeleteModalOpen(true);
        }
        break;
      case 'manageFeatures':
        console.log("id to manage access", rowData.id);
        navigate(`/user/role/${rowData.id}`, { state: { id: rowData.id } });
        break;
      default:
        break;
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    try {
      await deleteRole(roleToDelete.id);
      fetchRoles(); // Refresh role list
    } catch (error) {
      console.error("Error deleting role:", error);
    } finally {
      setIsDeleteModalOpen(false);
      setRoleToDelete(null);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setRoleToDelete(null);
  };

  // Define custom actions for table
  const getCustomActions = () => {
    return [
      {
        id: 'edit',
        icon: Update_Icon,
        label: t('Update')
      },
      {
        id: 'delete',
        icon: Delete_Icon,
        label: t('Delete')
      },
      {
        id: 'manageFeatures',
        icon: Assign_Icon,
        label: t('Assign Features')
      }
    ];
  };

  // Handle edit field change
  const handleEditChange = (field, value) => {
    if (editedRole) {
      // Map table field names to role object property names
      const fieldMapping = {
        'RoleAccess': 'name',
        'Description': 'description'
      };

      const roleField = fieldMapping[field] || field;

      setEditedRole(prev => ({
        ...prev,
        [roleField]: value
      }));

      // Update tableData to reflect changes immediately in UI
      const updatedTableData = [...tableData];
      if (editableRowIndex !== null && updatedTableData[editableRowIndex]) {
        updatedTableData[editableRowIndex] = {
          ...updatedTableData[editableRowIndex],
          [field]: value
        };
        setTableData(updatedTableData);
      }
    }
  };

  // Handle edit save
  const handleEditSave = async (rowIndex) => {
    try {
      if (!editedRole) return;

      // Update role on the server
      await updateRole(editedRole);

      // Update local state
      setRoleList(prev => prev.map(role =>
        role.id === editedRole.id ? { ...role, ...editedRole } : role
      ));

      setFilteredRoles(prev => prev.map(role =>
        role.id === editedRole.id ? { ...role, ...editedRole } : role
      ));

      // Reset edit state
      setEditableRowIndex(null);
      setEditedRole(null);

      // Refresh roles to get the latest data
      fetchRoles();
    } catch (error) {
      console.error("Error saving role:", error);
      // alert("Failed to save changes. Please try again.");
    }
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditableRowIndex(null);
    setEditedRole(null);
    // Reset table data to original values
    setTableData(formatDataForTable());
  };

  // Fetch roles data from API
  const fetchRoles = async () => {
    setIsLoading(true);
    try {
      const response = await getAllRoles();
      setRoleList(response.roles);
      setFilteredRoles(response.roles);
    } catch (error) {
      console.error("Error fetching roles:", error);
      navigate("/"); // Redirect to home on error
    } finally {
      setIsLoading(false);
    }
  };

  // Handle role creation
  const handleRoleAdded = () => {
    fetchRoles(); // Refresh roles after adding
  };

  // Pagination handler
  const handlePageChange = (page) => {
    // Cancel any active editing when changing pages
    if (editableRowIndex !== null) {
      handleEditCancel();
    }
    setCurrentPage(page);
  };

  // Effect to load roles on component mount
  useEffect(() => {
    fetchRoles();
  }, []);

  // Effect for search and filter
  useEffect(() => {
    if (roleList.length > 0) {
      let result = [...roleList];

      if (searchText) {
        result = searchInTheTable(searchText, result);
      }

      if (filterValue) {
        result = filterBy(filterValue, 'name', result);
      }

      setFilteredRoles(result);
    }
  }, [searchText, filterValue, roleList]);


  const translatePath = (path) => {
    // Split the path into segments
    const segments = path.split('>');

    // Translate each segment and join with '>'
    return segments
      .map(segment => t(segment.trim()))
      .join(' > ');
  };
  return (
    <div className="role-main-container">

      <div className="subtitle-container" style={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        width: '100%',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          width: '100%',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div className="subtitle-left-content">
            <Box>
              <span className="subtitle-text">{t("users")}</span>
              <span className="subtitle-path">{translatePath("users > Manage Roles")}</span>
            </Box>
          </div>

          <div className="total-roles-box" style={{
            width: 'auto',
            minWidth: '200px',
            maxWidth: '313px',
            height: 'auto',
            borderRadius: '10px',
            padding: '12px 24px',
            background: '#FFFFFF',
            border: '1px solid #A5DCF3',
            boxShadow: '-2px 1px 5px 1px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <span style={{
              fontFamily: 'Roboto',
              fontWeight: '500',
              fontSize: '16px',
              lineHeight: '100%',
              letterSpacing: '0%'
            }}>
              {t("Total Roles")}:
            </span>
            <span style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#183059'
            }}>
              {roleList.length}
            </span>
          </div>
        </div>
      </div>


      <RoleAdd
        createRole={createRole}
        onRoleAdded={handleRoleAdded}
      />

      <div className="table_container">
        <SearchFilter
          searchPlaceholder="search"
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        <RoleTable
          headers={headers}
          data={tableData}
          onActionClick={handleActionClick}
          actions={getCustomActions()}
          loading={isLoading}
          // Inline editing props
          editableRow={editableRowIndex}
          editableFields={["RoleAccess", "Description"]}
          onEditChange={handleEditChange}
          onEditSave={handleEditSave}
          onEditCancel={handleEditCancel}
        />

        {/* <Pagination
          totalItems={filteredRoles?.length || 0}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        /> */}
      </div>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Role"
        message={`Are you sure you want to delete ${roleToDelete?.name || 'this role'}? This action cannot be undone.`}
      />
    </div>
  );
}